import {
  $createLineBreakNode,
  $createParagraphNode,
  $createTextNode,
  $getRoot,
} from 'lexical';
import type { LexicalEditor } from './lexical-types';
import { parseStoredContent } from './rich-text-content';

/**
 * Loads a stored rich-text value into a live Lexical editor. JSON values are parsed straight into an
 * editor state; legacy plain-text values are rebuilt with paragraphs (`\n\n`) and line breaks (`\n`).
 *
 * Ported from the Gold app (cashpile/apps/cashvest).
 */
export function applyStoredContentToEditor(editor: LexicalEditor, value: string): void {
  const parsed = parseStoredContent(value);

  if (parsed.kind === 'json') {
    editor.setEditorState(editor.parseEditorState(parsed.state));
    return;
  }

  // `discrete: true` forces a synchronous commit so callers can read the resulting state (and set
  // their change-tracking baseline) immediately, instead of on a later deferred flush.
  editor.update(
    () => {
      const root = $getRoot();
      root.clear();

      const paragraphs = parsed.text.split('\n\n');
      for (const paragraph of paragraphs) {
        const paragraphNode = $createParagraphNode();
        paragraph.split('\n').forEach((line, index) => {
          if (index > 0) paragraphNode.append($createLineBreakNode());
          if (line) paragraphNode.append($createTextNode(line));
        });
        root.append(paragraphNode);
      }

      if (root.getChildrenSize() === 0) {
        root.append($createParagraphNode());
      }
    },
    { discrete: true },
  );
}
